import { ArrowLeftRight, CheckCircle2, CircleSlash2, Search, TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { EmptyState } from "../components/EmptyState";
import { FilterChips } from "../components/FilterChips";
import { LastUpdated } from "../components/LastUpdated";
import { PageHeader } from "../components/PageHeader";
import { WarningCallout } from "../components/WarningCallout";
import { WordingCard } from "../components/WordingCard";
import { languageLabels, wordingFormats } from "../data/categories";
import { cautionPhrases, prohibitedGuidance, projectWordings, recommendedWords } from "../data/projectLanguage";
import { normalizeSearch } from "../lib/search";

const statusOptions = [
  { id: "all", label: "Все статусы" },
  { id: "approved", label: "Одобрено" },
  { id: "needs-review", label: "Требует проверки" },
  { id: "draft", label: "Черновик" },
];

export function WordingPage() {
  const [query, setQuery] = useState("");
  const [format, setFormat] = useState("all");
  const [language, setLanguage] = useState("all");
  const [status, setStatus] = useState("all");
  const [audience, setAudience] = useState("all");
  const [compare, setCompare] = useState(false);

  const audiences = Array.from(new Set(projectWordings.map((item) => item.audience)));
  const wordings = useMemo(() => {
    const needle = normalizeSearch(query);
    return projectWordings.filter((wording) => (
      (!needle || normalizeSearch(`${wording.title} ${wording.text} ${wording.audience}`).includes(needle))
      && (format === "all" || wording.format === format)
      && (language === "all" || wording.language === language)
      && (status === "all" || wording.status === status)
      && (audience === "all" || wording.audience === audience)
    ));
  }, [query, format, language, status, audience]);

  const official = projectWordings.find((item) => item.id === "one-sentence-ru")!;
  const social = projectWordings.find((item) => item.id === "social-media-ru")!;

  return (
    <div className="page">
      <PageHeader
        eyebrow="Раздел 03"
        title="Как правильно говорить о проекте"
        description="Готовые формулировки для разных форматов, аудиторий и каналов."
        actions={
          <button type="button" className={`button button--secondary ${compare ? "is-selected" : ""}`} onClick={() => setCompare((value) => !value)}>
            <ArrowLeftRight size={17} /> Сравнить
          </button>
        }
      />
      <LastUpdated date="2026-07-15" section />

      {compare && (
        <section className="comparison-panel">
          <div className="comparison-panel__heading">
            <span className="eyebrow">Режим сравнения</span>
            <h2>Официально и для соцсетей</h2>
          </div>
          <div className="comparison-grid">
            <article>
              <span>Официальный вариант</span>
              <p>{official.text}</p>
            </article>
            <article>
              <span>Упрощённый вариант</span>
              <p>{social.text}</p>
            </article>
          </div>
        </section>
      )}

      <section className="filters-panel" aria-label="Фильтры формулировок">
        <div className="filters-panel__search">
          <Search size={19} aria-hidden="true" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Найти формулировку" aria-label="Найти формулировку" />
        </div>
        <FilterChips
          label="Формат"
          value={format}
          onChange={setFormat}
          options={[{ id: "all", label: "Все форматы" }, ...wordingFormats]}
        />
        <div className="filter-row">
          <label>
            <span>Язык</span>
            <select value={language} onChange={(event) => setLanguage(event.target.value)}>
              <option value="all">Все языки</option>
              {Object.entries(languageLabels).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          </label>
          <label>
            <span>Статус</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              {statusOptions.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </label>
          <label>
            <span>Аудитория</span>
            <select value={audience} onChange={(event) => setAudience(event.target.value)}>
              <option value="all">Все аудитории</option>
              {audiences.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
        </div>
      </section>

      {wordings.length > 0 ? (
        <div className="wording-grid">{wordings.map((wording) => <WordingCard key={wording.id} wording={wording} />)}</div>
      ) : (
        <EmptyState description="Для выбранных фильтров пока нет подготовленной формулировки." />
      )}

      <section className="language-guidance">
        <div className="guidance-card guidance-card--recommended">
          <div className="guidance-card__heading"><CheckCircle2 size={22} /><h2>Рекомендуемые слова</h2></div>
          <div className="tag-cloud">{recommendedWords.map((item) => <span key={item}>{item}</span>)}</div>
        </div>
        <div className="guidance-card guidance-card--caution">
          <div className="guidance-card__heading"><TriangleAlert size={22} /><h2>Требуют осторожности</h2></div>
          <div className="tag-cloud">{cautionPhrases.map((item) => <span key={item}>{item}</span>)}</div>
          <WarningCallout>Эти утверждения нельзя использовать без отдельного документального или официального подтверждения.</WarningCallout>
        </div>
        <div className="guidance-card guidance-card--prohibited">
          <div className="guidance-card__heading"><CircleSlash2 size={22} /><h2>Нельзя говорить</h2></div>
          <ul className="prohibited-list">{prohibitedGuidance.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
      </section>
    </div>
  );
}
